<template>
  <div id="app" class="v-flex">
    <ContainerWithNav v-if="canRenderContainer">
      <StartupLoadError v-if="hasStartupLoadError" />
      <router-view v-else-if="isInfoStored" />
    </ContainerWithNav>
    <InactiveNotice />
  </div>
</template>

<script>
import ContainerWithNav from '@/components/PageContainers/ContainerWithNav/ContainerWithNav.vue'
import InactiveNotice from '@/components/Authentication/InactiveNotice/InactiveNotice.vue'
import StartupLoadError from '@/components/Authentication/StartupLoadError/StartupLoadError.vue'
import { eventBus } from '@/plugins/eventbus'
import { debounce } from 'lodash-es'
import { session } from '@/utils/session'
import { usesFirebaseSession } from '@/utils/session-config'

export default {
  components: {
    ContainerWithNav,
    InactiveNotice,
    StartupLoadError
  },

  data() {
    return {
      activityHandler: null,
      activityEvents: [],
      boundVisibilityHandler: null
    }
  },

  computed: {
    canRenderContainer() {
      return (
        !usesFirebaseSession() ||
        this.$route.meta?.publicRoute ||
        this.$store.getters.sessionReady ||
        this.hasStartupLoadError
      )
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    },

    hasStartupLoadError() {
      return (
        !this.$route.meta?.publicRoute &&
        (this.$store.getters.isAuthenticated ||
          (usesFirebaseSession() &&
            this.$store.state.authStore.session.status === 'error')) &&
        this.$store.state.dataStore.startupLoadError != null
      )
    },

    isInfoStored() {
      if (this.$route.meta?.publicRoute) {
        return true
      }
      if (usesFirebaseSession()) return this.$store.getters.sessionReady
      if (this.$store.getters.isAuthenticated) {
        return (
          this.$store.state.dataStore.isRefreshComplete ||
          this.$store.getters.currentMerchant != null
        )
      } else {
        return true
      }
    }
  },

  watch: {
    // Every logout path, not only the error screen's Sign out: a stale error would otherwise
    // replace the router view after the next sign-in.
    isAuthenticated(isAuthenticated) {
      if (!isAuthenticated) {
        this.$store.commit('setStartupLoadError', null)
      }
    }
  },

  created() {
    eventBus.on('sessionRevocationFailed', this.revocationFailed)
    this.$store.dispatch('heartbeat')

    const legacyActivity = debounce(() => {
      if (usesFirebaseSession() || !this.$store.getters.isAuthenticated) return
      if (this.$store.getters.isInactive) {
        this.$store.dispatch('continueSession')
      } else {
        this.$store.commit('setLastActivity')
      }
    }, 60000)
    this.activityHandler = event => {
      if (usesFirebaseSession()) {
        legacyActivity.cancel()
        if (event.isTrusted) session.humanActivity()
      } else {
        legacyActivity()
      }
    }

    this.activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'click'
    ]

    this.activityEvents.forEach(event =>
      window.addEventListener(event, this.activityHandler, { passive: true })
    )

    this.boundVisibilityHandler = this.visibilityHandler.bind(this)
    document.addEventListener('visibilitychange', this.boundVisibilityHandler)
  },

  beforeDestroy() {
    eventBus.off('sessionRevocationFailed', this.revocationFailed)
    if (this.activityEvents && this.activityHandler) {
      this.activityEvents.forEach(event =>
        window.removeEventListener(event, this.activityHandler)
      )
    }
    document.removeEventListener(
      'visibilitychange',
      this.boundVisibilityHandler
    )
  },

  methods: {
    revocationFailed() {
      this.$toasted.global.error({
        message:
          'You signed out locally. We could not end refresh sessions on other devices.'
      })
    },
    visibilityHandler() {
      if (usesFirebaseSession()) {
        session.checkIdle()

        return
      }
      if (document.visibilityState !== 'visible') {
        return
      }
      if (!this.$store.getters.isAuthenticated) {
        return
      }

      if (this.$store.getters.isInactive) {
        this.$store.dispatch('continueSession')
      } else {
        this.$store.commit('setLastActivity')
      }
    }
  }
}
</script>

<style lang="scss">
html,
body {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: stretch;
  overflow: auto;

  background: transparent linear-gradient(340deg, $dark-blue 0%, $blue 100%) 0%
    0% no-repeat padding-box;
}
</style>
